import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { notificationSchedules as initialSchedules } from '../../data/notificationSchedules';
import { notificationTemplates } from '../../data/notificationTemplates';

const { FiClock, FiToggleLeft, FiToggleRight, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX } = FiIcons;

const triggerColors = {
  not_started: 'bg-slate-100 text-slate-600 border-slate-200',
  due_soon: 'bg-amber-100 text-amber-700 border-amber-200',
  overdue: 'bg-red-100 text-red-700 border-red-200',
  phishing_followup: 'bg-orange-100 text-orange-700 border-orange-200',
  compliance_deadline: 'bg-violet-100 text-violet-700 border-violet-200',
  manager_escalation: 'bg-blue-100 text-blue-700 border-blue-200',
};

const TRIGGER_OPTIONS = [
  { value: 'not_started', label: 'Training Not Started' },
  { value: 'due_soon', label: 'Due Soon' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'phishing_followup', label: 'Phishing Follow-Up' },
  { value: 'compliance_deadline', label: 'Compliance Deadline' },
  { value: 'manager_escalation', label: 'Manager Escalation' },
];

const Toggle = ({ enabled, onToggle }) => (
  <button onClick={onToggle} className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}>
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
  </button>
);

const AddScheduleForm = ({ onSave, onCancel }) => {
  const [form, setForm] = useState({ name: '', trigger: 'due_soon', templateId: notificationTemplates[0].id, daysBefore: 7, daysAfterAssignment: 3, daysAfterDue: 1, recipients: ['learner'], description: '' });
  const upd = patch => setForm(f => ({ ...f, ...patch }));
  const canSave = form.name.trim().length > 0;

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
      <h4 className="text-sm font-semibold text-blue-800">New Schedule Rule</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Rule Name</label>
          <input value={form.name} onChange={e => upd({ name: e.target.value })} placeholder="e.g. Due Soon — 5 Days"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Trigger</label>
          <select value={form.trigger} onChange={e => upd({ trigger: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white">
            {TRIGGER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Email Template</label>
          <select value={form.templateId} onChange={e => upd({ templateId: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white">
            {notificationTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Days (before due / after assignment)</label>
          <input type="number" min={0} max={60} value={form.daysBefore}
            onChange={e => upd({ daysBefore: Number(e.target.value), daysAfterAssignment: Number(e.target.value), daysAfterDue: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
        <input value={form.description} onChange={e => upd({ description: e.target.value })} placeholder="Brief description of this rule"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
        <button onClick={() => canSave && onSave({ ...form, id: `NS${Date.now()}`, isActive: true })} disabled={!canSave}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg ${canSave ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
          <SafeIcon icon={FiCheck} /> Save Rule
        </button>
      </div>
    </motion.div>
  );
};

const ScheduleManager = () => {
  const [schedules, setSchedules] = useState(initialSchedules);
  const [showAdd, setShowAdd] = useState(false);

  const toggle = id => setSchedules(prev => prev.map(s => s.id === id ? { ...s, isActive: !s.isActive } : s));
  const remove = id => setSchedules(prev => prev.filter(s => s.id !== id));
  const addSchedule = s => { setSchedules(prev => [...prev, s]); setShowAdd(false); };

  const getTriggerLabel = t => TRIGGER_OPTIONS.find(o => o.value === t)?.label || t;
  const getTemplate = id => notificationTemplates.find(t => t.id === id);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-blue-500" />
          <h3 className="text-sm font-semibold text-slate-700">Automated Schedule Rules</h3>
          <span className="text-xs text-slate-400 font-medium">{schedules.filter(s => s.isActive).length} active</span>
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors">
          <SafeIcon icon={FiPlus} /> Add Rule
        </button>
      </div>

      <AnimatePresence>{showAdd && <AddScheduleForm onSave={addSchedule} onCancel={() => setShowAdd(false)} />}</AnimatePresence>

      <div className="space-y-2">
        {schedules.map(s => {
          const tmpl = getTemplate(s.templateId);
          const days = s.daysBefore ?? s.daysAfterAssignment ?? s.daysAfterDue ?? 0;
          return (
            <div key={s.id} className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${s.isActive ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
              <Toggle enabled={s.isActive} onToggle={() => toggle(s.id)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${triggerColors[s.trigger] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {getTriggerLabel(s.trigger)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                <p className="text-xs text-slate-400 mt-0.5">Template: <span className="text-slate-600 font-medium">{tmpl?.name}</span> · {days}d timing · Recipients: {s.recipients.join(', ')}</p>
              </div>
              <button onClick={() => remove(s.id)} className="text-slate-300 hover:text-red-400 transition-colors flex-shrink-0">
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduleManager;